import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Link2, AlertTriangle, Building2 } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { toast } from "sonner";

const opts = { withCredentials: true };

// Modal "Vincular Adicional a Obra" — aparece al intentar aprobar un budget is_adicional.
export default function AdicionalObraModal({ budgetId, budgetTotal, clienteId, onClose, onLinked }) {
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        let list = [];
        if (clienteId) {
          const { data } = await axios.get(`${API}/obras?status=activa&cliente_id=${clienteId}`, opts);
          list = Array.isArray(data) ? data : [];
        }
        if (!list.length) {
          const { data } = await axios.get(`${API}/obras?status=activa`, opts);
          list = Array.isArray(data) ? data : [];
        }
        if (!cancelled) setObras(list);
      } catch {
        if (!cancelled) toast.error("Error cargando obras");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clienteId]);

  const selected = useMemo(() => obras.find(o => o._id === selectedId), [obras, selectedId]);
  const add = Number(budgetTotal || 0);
  const before = Number(selected?.total || 0);
  const after = before + add;

  const anexar = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/budgets/${budgetId}/aprobar-adicional`, { obra_padre_id: selectedId }, opts);
      toast.success(`Adicional anexado a ${selected?.codigo || "la obra"}`);
      onLinked?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al anexar el adicional");
    } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="adicional-obra-modal">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-fuchsia-50 text-fuchsia-600 flex-shrink-0"><Link2 size={20} /></div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Presupuesto Adicional</h2>
              <p className="text-xs text-gray-500 mt-0.5">Seleccioná la Obra a la que se va a anexar esta adenda.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Obra padre</label>
            <select
              value={selectedId} onChange={e => setSelectedId(e.target.value)} disabled={loading}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-500"
              data-testid="adicional-obra-select"
            >
              <option value="">{loading ? "Cargando obras..." : "— Seleccioná una obra activa —"}</option>
              {obras.map(o => (
                <option key={o._id} value={o._id}>{o.codigo} — {o.nombre} ({o.cliente_nombre || "Sin cliente"})</option>
              ))}
            </select>
            {!loading && obras.length === 0 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5"><AlertTriangle size={13} /> No hay obras activas disponibles para anexar.</p>
            )}
          </div>

          {selected && (
            <div className="bg-fuchsia-50/70 border border-fuchsia-100 rounded-xl p-4 text-sm" data-testid="adicional-summary">
              <p className="text-gray-700 flex items-center gap-1.5 mb-2"><Building2 size={14} className="text-fuchsia-600" /><strong>{selected.nombre}</strong></p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Esta adenda agrega <strong className="text-fuchsia-700">{formatPYG(add)}</strong> al total de la obra <strong>{selected.codigo}</strong>,
                llevándolo de <strong>{formatPYG(before)}</strong> a <strong className="text-gray-900">{formatPYG(after)}</strong>.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium" data-testid="adicional-cancel-btn">Cancelar</button>
            <button onClick={anexar} disabled={!selectedId || saving} className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="adicional-anexar-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Anexar a la Obra
            </button>
          </div>
        </div>
      </div>
    </div>, document.body);
}
